import React from 'react';
import { Box, Typography, Tooltip, LinearProgress } from '@mui/material';
import { Cpu, MemoryStick, Globe, Clock, Activity, AlertTriangle, CheckCircle2, ShieldAlert, Wifi, Users, Router } from 'lucide-react';
import GlassPaper from '../../GlassPaper';
import StatPill from './StatPill';

function utilColor(v) {
    if (v == null) return '#64748b';
    if (v >= 85) return '#ef4444';
    if (v >= 70) return '#f59e0b';
    return '#10b981';
}
function fmtUptime(sec) {
    if (!sec) return '—';
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    if (d > 0) return `${d}d ${h}h`;
    const m = Math.floor((sec % 3600) / 60);
    return `${h}h ${m}m`;
}
function fmtBps(bps) {
    if (bps == null) return '—';
    if (bps < 1000) return `${bps} bps`;
    if (bps < 1e6) return `${(bps / 1000).toFixed(1)} Kbps`;
    if (bps < 1e9) return `${(bps / 1e6).toFixed(1)} Mbps`;
    return `${(bps / 1e9).toFixed(2)} Gbps`;
}
function latencyColor(ms) {
    if (ms == null) return '#64748b';
    if (ms >= 100) return '#ef4444';
    if (ms >= 30) return '#f59e0b';
    return '#10b981';
}

const SEV_LABEL = ['Not classified', 'Information', 'Warning', 'Average', 'High', 'Disaster'];
const SEV_TONE = ['muted', 'info', 'warn', 'warn', 'crit', 'crit'];

export default function GatewayCard({ host, onClick }) {
    const m = host.metrics || {};
    const wanUp = m.wanStatus === 1;
    const reachable = host.available === 1 || m.icmpPing === 1 || m.icmpReachable === 1;
    const online = reachable && wanUp;
    const statusTone = online ? 'good' : (reachable ? 'warn' : 'crit');
    const statusLabel = !reachable ? 'Down' : (wanUp ? 'Up' : 'WAN ↓');
    const coverageBad = host.metricCoverage && !host.metricCoverage.ok && host.metricCoverage.expected?.length > 0;
    const templateBad = !host.templateOk;
    const apsBad = m.apsDisconnected != null && m.apsDisconnected > 0;
    const topProblem = (host.problems || []).slice().sort((a, b) => (b.severity || 0) - (a.severity || 0))[0];
    const clickable = typeof onClick === 'function';

    return (
        <GlassPaper
            onClick={clickable ? onClick : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
            tabIndex={clickable ? 0 : undefined}
            role={clickable ? 'button' : undefined}
            sx={{
                p: { xs: 1.75, sm: 2 },
                height: '100%',
                display: 'flex', flexDirection: 'column', gap: 1.25,
                position: 'relative',
                overflow: 'hidden',
                transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                cursor: clickable ? 'pointer' : 'default',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    ...(clickable && { boxShadow: '0 10px 24px rgba(59,130,246,0.18)', borderColor: 'rgba(96,165,250,0.45)' }),
                },
                '&:focus-visible': clickable ? { outline: '2px solid rgba(96,165,250,0.55)', outlineOffset: 2 } : undefined,
            }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Router size={14} style={{ opacity: 0.7 }} />
                        <Typography sx={{
                            fontSize: { xs: 14, sm: 14.5 }, fontWeight: 700, lineHeight: 1.2,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{host.name}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 11, opacity: 0.6, mt: 0.25 }}>
                        {host.ip || host.host} · Gateway
                    </Typography>
                </Box>
                <StatPill tone={statusTone} icon={<Activity size={11} />}>
                    {statusLabel}
                </StatPill>
            </Box>

            {(templateBad || coverageBad || apsBad) && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {templateBad && (
                        <Tooltip title="No Zabbix template assigned"><span><StatPill tone="warn" icon={<ShieldAlert size={11} />}>Template</StatPill></span></Tooltip>
                    )}
                    {coverageBad && (
                        <Tooltip title={`Missing metrics: ${host.metricCoverage.missing.join(', ')}`}>
                            <span><StatPill tone="warn" icon={<AlertTriangle size={11} />}>Metrics</StatPill></span>
                        </Tooltip>
                    )}
                    {apsBad && (
                        <Tooltip title={`${m.apsDisconnected} of ${m.apsAdopted} APs disconnected`}>
                            <span><StatPill tone="warn" icon={<Wifi size={11} />}>{m.apsDisconnected} AP{m.apsDisconnected > 1 ? 's' : ''} down</StatPill></span>
                        </Tooltip>
                    )}
                </Box>
            )}

            {topProblem && (
                <Tooltip title={topProblem.name}>
                    <Box>
                        <StatPill tone={SEV_TONE[topProblem.severity] || 'warn'} icon={<AlertTriangle size={11} />}>
                            {SEV_LABEL[topProblem.severity] || 'Problem'}
                        </StatPill>
                    </Box>
                </Tooltip>
            )}

            {/* Top metrics row: CPU / Memory / WAN latency */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mt: 0.5 }}>
                <PctMetric icon={<Cpu size={13} />} label="CPU" value={m.cpuPct} />
                <PctMetric icon={<MemoryStick size={13} />} label="Memory" value={m.memPct} />
                <Tile
                    icon={<Globe size={13} />}
                    label="WAN Latency"
                    value={m.wanLatencyMs != null ? `${m.wanLatencyMs.toFixed(0)}ms` : '—'}
                    color={latencyColor(m.wanLatencyMs)}
                />
            </Box>

            {/* WAN throughput row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, fontSize: 11.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.85 }}>
                    <span style={{ opacity: 0.5 }}>↓</span>
                    <span style={{ fontWeight: 600 }}>{fmtBps(m.wanRxBps)}</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.85 }}>
                    <span style={{ opacity: 0.5 }}>↑</span>
                    <span style={{ fontWeight: 600 }}>{fmtBps(m.wanTxBps)}</span>
                </Box>
            </Box>

            {/* Bottom info row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 'auto', pt: 0.5, flexWrap: 'wrap', fontSize: 11.5, opacity: 0.78 }}>
                <Tooltip title="Uptime">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Clock size={12} /> {fmtUptime(m.uptimeSec)}
                    </Box>
                </Tooltip>
                {(m.wanClients != null || m.wlanClients != null) && (
                    <Tooltip title={`Wired: ${m.wanClients ?? 0} · Wireless: ${m.wlanClients ?? 0}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Users size={12} /> {(m.wanClients ?? 0) + (m.wlanClients ?? 0)}
                        </Box>
                    </Tooltip>
                )}
                {m.apsAdopted != null && (
                    <Tooltip title={`${m.apsAdopted} APs adopted, ${m.apsDisconnected ?? 0} disconnected`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Wifi size={12} /> {m.apsAdopted - (m.apsDisconnected ?? 0)}/{m.apsAdopted}
                        </Box>
                    </Tooltip>
                )}
                {!topProblem && !templateBad && !coverageBad && !apsBad && online && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', color: '#34d399' }}>
                        <CheckCircle2 size={12} /> Healthy
                    </Box>
                )}
            </Box>
        </GlassPaper>
    );
}

function PctMetric({ icon, label, value }) {
    const v = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : null;
    const color = utilColor(v);
    return (
        <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, mb: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.7, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>
                    {icon}{label}
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color }}>
                    {v != null ? `${v.toFixed(0)}%` : '—'}
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={v ?? 0}
                sx={{
                    height: 5,
                    borderRadius: 999,
                    bgcolor: 'rgba(148,163,184,0.16)',
                    '& .MuiLinearProgress-bar': { borderRadius: 999, backgroundColor: color },
                }}
            />
        </Box>
    );
}

function Tile({ icon, label, value, color }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, mb: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.7, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>
                    {icon}{label}
                </Box>
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: color || '#cbd5e1' }}>
                {value}
            </Typography>
        </Box>
    );
}
